import Block from '@/components/Block'
import BookmarkedItem from '@/components/BookmarkedItem'
import Button from '@/components/Button'
import GsuLoader from '@/components/GsuLoader'
import Layout, { Stack } from '@/components/Layout'
import { coreLayoutContainer } from '@/constants/measured-scope'
import { BookmarkedItemsPageSection, ItemType, PageInfo } from '@/shared/api-generated-types'
import { useCallback, useEffect, useRef, useState } from 'react'
import unionBy from 'lodash/unionBy'
import NoBookmarksFound from '@/components/NoBookmarksFound'
import { useBookmarksTab } from '@/context/bookmark'

export interface Props {
  items: BookmarkedItemsPageSection['items']
  pageInfo?: Partial<PageInfo>
  onLoadMore?: () => void
  fetching?: boolean
}

const BookmarkedItemsBlock = ({ items, onLoadMore, pageInfo, fetching }: Props): JSX.Element => {
  const tab = useBookmarksTab()
  const canShowLoadMore = onLoadMore && pageInfo?.hasNextPage
  const [localItems, setLocalItems] = useState(items)
  const prevTabRef = useRef(tab)

  useEffect(() => {
    if (prevTabRef.current !== tab) {
      setLocalItems(items)
      prevTabRef.current = tab
    } else {
      setLocalItems((prev) => unionBy(prev, items, 'id'))
    }
  }, [tab, items])

  const handleRemove = useCallback((id: string) => {
    setLocalItems((prev) => prev.filter((item) => item.id !== id))
  }, [])

  const filteredItems = localItems.filter((item) =>
    tab === ItemType.Class ? item.__typename === 'VodClassCard' : item.__typename !== 'VodClassCard',
  )

  return (
    <Block className={`px-sm px-lg-gte-sm px-xl-gte-lg px-xxl-gte-xxl !py-0`}>
      <Stack x className={coreLayoutContainer} justifyContent="center">
        <Stack y>
          {filteredItems.length > 0 ? (
            <div>
              {filteredItems.map((item) => (
                <BookmarkedItem
                  key={item.id}
                  {...item}
                  type={item.__typename === 'VodClassCard' ? ItemType.Class : ItemType.Article}
                  onRemove={handleRemove}
                />
              ))}
            </div>
          ) : (
            <Layout>
              <NoBookmarksFound />
            </Layout>
          )}
        </Stack>
      </Stack>
      {canShowLoadMore && (
        <div className="pt ta-c">
          <Button
            label={
              fetching ? (
                <>
                  <GsuLoader size={18} className="mr-sm" />
                  Loading...
                </>
              ) : (
                'Load more'
              )
            }
            size="lg"
            onClick={!fetching ? onLoadMore : undefined}
            component="button"
            disabled={fetching}
            theme="noBackground"
          />
        </div>
      )}
    </Block>
  )
}

export default BookmarkedItemsBlock
